/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import { getColorFromColorName, getFgColorFromBgColorName } from 'bundles/cds-labs/components/Modules/utils/colors';
import parseLayoutOptions from 'bundles/cds-labs/components/Modules/utils/parseLayoutOptions';

import _t from 'i18n!nls/cds-labs';

export const NAV_BREAKPOINT = 1200;
export const MENU_HEIGHT = '100px';
export const MENU_HEIGHT_MOBILE = '56px';

export const HEIGHT_VALUES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
} as const;

export const MODULE_LAYOUTS = {
  text10image2: 'text10/image2',
  text9image3: 'text9/image3',
  text8image4: 'text8/image4',
  text7image5: 'text7/image5',
  text6image6: 'text6/image6',
  text5image7: 'text5/image7',
  text4image8: 'text4/image8',
  text3image9: 'text3/image9',
  text2image10: 'text2/image10',
  image10text2: 'image10/text2',
  image9text3: 'image9/text3',
  image8text4: 'image8/text4',
  image7text5: 'image7/text5',
  image6text6: 'image6/text6',
  image5text7: 'image5/text7',
  image4text8: 'image4/text8',
  image3text9: 'image3/text9',
  image2text10: 'image2/text10',
  textTopImageBottom: 'text-top/image-bottom',
  imageTopTextBottom: 'image-top/text-bottom',
  noImage: 'no-image',
  noImageCentered: 'no-image-centered',
} as const;

type ModuleLayoutValue = (typeof MODULE_LAYOUTS)[keyof typeof MODULE_LAYOUTS];

export type ModuleLayoutConfig = {
  layout?: ModuleLayoutValue | null;

  // TODO: since we want to remove the slug from modules, the ghost anchor will not be set properly.
  //       currently ghost anchor is not necessary, but if we need to add it back in the future,
  //       we may need to find a way to generate the anchorId from the module title or something else.
  anchorId?: string | null;
  layoutOptions?: Array<string>;
  backgroundColor?: string | null;
  backgroundImage?: ImageType | null;
  preloadImage?: boolean;
  lazyLoadImage?: boolean;
  removeTopPadding?: boolean;
  removeBottomPadding?: boolean;
  height?: string | null;
  showRoundedCorners?: boolean | null;
  showBottomBorder?: boolean | null;
  topMargin?: string | null;
  // custom styles for the image panel
  // currently only used for the image panel in the hero module with text6/image6 and image6/text6 layout
  customImagePanelStyles?: SerializedStyles[];
  // whether PageGridContainer has been applied on the page
  // if true, the ModuleLayout will not nest another PageGridContainer
  // which is used to align containter modules ( i.e. showRoundedCorners set to true) with other modules on the page
  isPageGridContainerAppliedOnPage?: boolean;
};

const ModuleLayoutProperties: Array<keyof ModuleLayoutConfig> = [
  'layout',
  'layoutOptions',
  'backgroundColor',
  'backgroundImage',
  'preloadImage',
  'lazyLoadImage',
  'removeTopPadding',
  'removeBottomPadding',
  'height',
  'showRoundedCorners',
  'topMargin',
  'showBottomBorder',
  'isPageGridContainerAppliedOnPage',
];

type Props = {
  // Any block assembly properties that can be trusted can be attached here
  assembly?: ModuleLayoutConfig;
  // End of assembly overrides
  renderImage?: () => React.ReactNode;
  renderContent: () => React.ReactNode;
  alignItems?: GridProps['alignItems'];
};

export type ButtonVariants = 'primary' | 'secondary' | 'ghost';

export const getModuleLayoutConfig = (props: Record<string, any>) => {
  const moduleLayoutConfig: ModuleLayoutConfig = {};
  ModuleLayoutProperties.forEach((property) => {
    if (props[property]) {
      moduleLayoutConfig[property] = props[property];
    }
  });

  return moduleLayoutConfig;
};

// handy shortcut for overriding layout with 'no-image'
export function noImage(assembly: ModuleLayoutConfig) {
  return { ...assembly, layout: MODULE_LAYOUTS.noImage };
}

// handy shortcut for overriding layoutOptions with 'no-y-padding'
export function noYPadding(assembly: ModuleLayoutConfig) {
  return { ...assembly, layoutOptions: [...(assembly.layoutOptions ?? []), 'no-y-padding'] };
}

// handy shortcut for overriding layoutOptions with 'top-image-on-phone'
export function topImageOnPhone(assembly: ModuleLayoutConfig) {
  return {
    ...assembly,
    layoutOptions: [...(assembly.layoutOptions ?? []), 'top-image-on-phone'],
  };
}

// handy shortcut for testing layout direction
export function isHorizontalLayout(assembly: { layout?: ModuleLayoutValue | null }) {
  if (!assembly.layout) return false;
  const verticalLayouts = [
    MODULE_LAYOUTS.imageTopTextBottom,
    MODULE_LAYOUTS.textTopImageBottom,
    MODULE_LAYOUTS.noImage,
  ];
  return !verticalLayouts.includes(assembly.layout);
}

export function isImageFirstLayout(assembly: { layout?: ModuleLayoutValue | null }) {
  if (!assembly.layout) return false;
  return assembly.layout?.startsWith('image') && assembly.layout !== MODULE_LAYOUTS.imageTopTextBottom;
}

export const toLayoutValue = (layout: string | null | undefined): ModuleLayoutValue | null | undefined => {
  if (layout === null) return null;
  if (layout === undefined) return undefined;
  return Object.values(MODULE_LAYOUTS).find((entry) => entry === layout);
};

const styles = {
  pageGridContainer: css`
    position: relative;

    && {
      padding-top: var(--cds-spacing-400);
      padding-bottom: var(--cds-spacing-400);

      ${breakpoints.down('md')} {
        padding-top: var(--cds-spacing-300);
        padding-bottom: var(--cds-spacing-300);
      }
    }
  `,
  roundedCorners: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;

    && {
      padding-top: var(--cds-spacing-200);
      padding-bottom: var(--cds-spacing-200);
      padding-left: var(--cds-spacing-400);
      padding-right: var(--cds-spacing-200);

      ${breakpoints.down('md')} {
        padding-left: var(--cds-spacing-200);
        padding-right: var(--cds-spacing-200);
      }
    }
  `,
  minHeight: (minHeight: number) => css`
    // apply the min-height to the child div of PageGridContainer to center the content vertically
    > div {
      min-height: calc(${minHeight}px - var(--cds-spacing-200) * 2);
    }

    ${breakpoints.down('md')} {
      > div {
        min-height: calc(${minHeight}px - var(--cds-spacing-100) * 2);
      }
    }

    // remove the min-height on mobile to prevent the image panel taking up space when there is no image inside.
    ${breakpoints.down('xs')} {
      > div {
        min-height: unset;
      }
    }
  `,
  panel: css`
    position: relative;
    align-self: stretch;
  `,
  imagePanel: css`
    display: flex;
    align-items: center;
  `,
  ghostAnchor: css`
    position: relative;
    width: 1px;
    height: 1px;

    /* Offset the margin top */
    top: calc(-${MENU_HEIGHT} + 1px);
    margin-top: -1px;

    ${breakpoints.down(NAV_BREAKPOINT)} {
      /* Offset the margin top */
      top: calc(-${MENU_HEIGHT_MOBILE} + 1px);
    }
  `,

  // Avoid double spacing for adjacent modules with same theme (background)
  noTopPadding: css`
    padding-top: 0 !important;
  `,

  noBottomPadding: css`
    padding-bottom: 0 !important;
  `,

  backgroundImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: end;
    justify-content: right;
    overflow: hidden;
  `,
  backgroundImageInner: (imageWidth: number, isRtl: boolean) => css`
    position: absolute;
    bottom: 0;
    left: ${breakpoints.values.lg - imageWidth}px;

    img {
      ${isRtl ? `transform: scaleX(-1);` : ''};
    }
  `,
  borderBottom: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  noImageCentered: css`
    text-align: center;
  `,
};

function ModuleLayout({ renderContent, renderImage, alignItems, assembly = {} }: Props) {
  const options = parseLayoutOptions(assembly.layoutOptions);
  const backgroundImage = assembly.backgroundImage ?? {};

  // reverse layout to make sure images always appear at the bottom on small screens
  let isReversed = isImageFirstLayout(assembly);
  if (options.topImageOnPhone) {
    isReversed = !isReversed;
  }

  const renderLayoutContent = (imageGrid: React.ReactNode, contentGrid: React.ReactNode) => {
    return options.topImageOnPhone ? (
      <>
        {imageGrid}
        {contentGrid}
      </>
    ) : (
      <>
        {contentGrid}
        {imageGrid}
      </>
    );
  };

  const renderLayout = () => {
    switch (assembly.layout) {
      case MODULE_LAYOUTS.image6text6:
      case MODULE_LAYOUTS.text6image6: {
        const contentGrid = (
          <Grid item xs={12} sm={6} css={[styles.panel]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} sm={6} css={[styles.panel, styles.imagePanel, assembly.customImagePanelStyles]}>
            {renderImage?.()}
          </Grid>
        );

        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image5text7:
      case MODULE_LAYOUTS.text7image5: {
        const contentGrid = (
          <Grid item xs={12} sm={7} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} sm={5} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image7text5:
      case MODULE_LAYOUTS.text5image7: {
        const contentGrid = (
          <Grid xs={12} sm={5} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} sm={7} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image4text8:
      case MODULE_LAYOUTS.text8image4: {
        const contentGrid = (
          <Grid item xs={12} sm={8} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} sm={4} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image8text4:
      case MODULE_LAYOUTS.text4image8: {
        const contentGrid = (
          <Grid item xs={12} sm={4} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} sm={8} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image9text3:
      case MODULE_LAYOUTS.text3image9: {
        const contentGrid = (
          <Grid item xs={12} md={3} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} md={9} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image10text2:
      case MODULE_LAYOUTS.text2image10: {
        const contentGrid = (
          <Grid item xs={12} lg={2} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} lg={10} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image2text10:
      case MODULE_LAYOUTS.text10image2: {
        const contentGrid = (
          <Grid item xs={12} lg={10} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} lg={2} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.image3text9:
      case MODULE_LAYOUTS.text9image3: {
        const contentGrid = (
          <Grid item xs={12} md={9} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid item xs={12} md={3} css={[styles.panel, styles.imagePanel]}>
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case MODULE_LAYOUTS.imageTopTextBottom:
        return (
          <>
            <Grid item xs={12} css={[styles.panel, styles.imagePanel]}>
              {renderImage?.()}
            </Grid>
            <Grid item xs={12} css={styles.panel}>
              {renderContent()}
            </Grid>
          </>
        );

      case MODULE_LAYOUTS.textTopImageBottom:
        return (
          <>
            <Grid item xs={12} css={styles.panel}>
              {renderContent()}
            </Grid>
            <Grid item xs={12} css={[styles.panel, styles.imagePanel]}>
              {renderImage?.()}
            </Grid>
          </>
        );
      case MODULE_LAYOUTS.noImageCentered:
        return (
          <Grid item xs={12} css={[styles.panel, styles.noImageCentered]}>
            {renderContent()}
          </Grid>
        );
      case MODULE_LAYOUTS.noImage:
      default:
        return (
          <Grid item xs={12} css={styles.panel}>
            {renderContent()}
          </Grid>
        );
    }
  };

  function ghostAnchor() {
    // This component is invisible but puts itself a variable number of pixels
    // above the block component for visually accurate anchoring.
    const { anchorId } = assembly;
    const id = anchorId || '';

    return <div data-testid={id} id={id} css={styles.ghostAnchor} />;
  }

  const backgroundColorStyles = {
    backgroundColor: getColorFromColorName(assembly.backgroundColor ?? '') ?? undefined,
    color: getFgColorFromBgColorName(assembly.backgroundColor ?? ''),
  };

  const showRoundedCorners = !!assembly.showRoundedCorners;

  let minHeightStyle;
  switch (assembly.height) {
    case HEIGHT_VALUES.SMALL:
      minHeightStyle = styles.minHeight(200);
      break;
    case HEIGHT_VALUES.MEDIUM:
      minHeightStyle = styles.minHeight(300);
      break;
    case HEIGHT_VALUES.LARGE:
      minHeightStyle = styles.minHeight(400);
      break;
    default:
      minHeightStyle = null;
  }

  const isRtl: boolean = isRightToLeft(_t.getLocale());

  const renderBackgroundImage = () =>
    backgroundImage.url ? (
      <Grid item css={styles.backgroundImage}>
        <div css={styles.backgroundImageInner(backgroundImage.width ?? 0, isRtl)}>
          <Image
            {...backgroundImage}
            style={{
              maxWidth: 'unset',
            }}
            shouldPreload={assembly.preloadImage}
            shouldLazyLoad={assembly.lazyLoadImage}
          />
        </div>
      </Grid>
    ) : null;

  const moduleContent = (
    <PageGridContainer
      data-testid="block_layout"
      alignItems={alignItems ?? 'flex-start'}
      justifyContent="center"
      direction={isReversed ? 'row-reverse' : 'row'}
      css={[
        styles.pageGridContainer,
        showRoundedCorners ? backgroundColorStyles : {},
        showRoundedCorners ? styles.roundedCorners : {},
        minHeightStyle,
        assembly.removeTopPadding ? styles.noTopPadding : null,
        assembly.removeBottomPadding ? styles.noBottomPadding : null,
      ]}
    >
      {renderBackgroundImage()}
      {renderLayout()}
    </PageGridContainer>
  );

  return (
    <>
      {ghostAnchor()}
      <div
        css={[
          !showRoundedCorners ? backgroundColorStyles : {},
          assembly.showBottomBorder ? styles.borderBottom : {},
          assembly.topMargin ? { marginTop: assembly.topMargin } : {},
        ]}
      >
        {showRoundedCorners && !assembly.isPageGridContainerAppliedOnPage ? (
          // If PageGridContainer has not been applied to the page, which is the common case,
          // nest the module inside another PageGridContainer to align container modules (i.e. showRoundedCorners set to true) with other modules on the page.
          // If PageGridContainer has been applied to the page, it'll be responsible for aligning the module with other modules on the page,
          // so we don't need to nest another PageGridContainer.
          <PageGridContainer>
            <Grid item xs={12}>
              <Grid container>{moduleContent}</Grid>
            </Grid>
          </PageGridContainer>
        ) : (
          moduleContent
        )}
      </div>
    </>
  );
}

export default ModuleLayout;
